/**
 * Wave 1C — the client-side mirror of `apps/api/src/platform/due-date.ts`:
 * BR-MD-09-03's "the due date must be worked out from the term the moment
 * the party is picked", previewed on the new-bill form before the server
 * has a draft to compute it on. The SERVER is the authority — a saved bill
 * carries the due date it derived — and this exists only so the box is
 * filled while the reader is still typing.
 *
 * Same four methods, same arithmetic (calendar dates as Y-M-D integers, UTC
 * so no timezone shifts a business day), and wave 1C's fallback: a supplier
 * with no term falls due `fallbackDays` after the bill date
 * (`purchasing.default_payment_term_days`); zero or null = due on issue.
 */

export interface TermShape {
  method: 'immediate' | 'days_after' | 'end_of_next_month' | 'day_of_month';
  days: number | null;
  dayOfMonth: number | null;
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

function lastDayOfMonth(year: number, month: number): number {
  // Month is 1..12; day 0 of the NEXT month is this month's last day.
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

function iso(year: number, month: number, day: number): string {
  return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

/** One term over one date. A null term = due on issue; an unparseable date comes back as given. */
export function dueDateFromTerm(term: TermShape | null, docDate: string): string {
  if (term === null || !ISO_DATE.test(docDate)) return docDate;
  const [y, m, d] = docDate.split('-').map((part) => parseInt(part, 10)) as [number, number, number];
  switch (term.method) {
    case 'immediate':
      return docDate;
    case 'days_after': {
      const date = new Date(Date.UTC(y, m - 1, d));
      // Calendar-day offset, not an amount.
      date.setUTCDate(date.getUTCDate() + (term.days ?? 0));
      return date.toISOString().slice(0, 10);
    }
    case 'end_of_next_month': {
      const nextY = m === 12 ? y + 1 : y;
      const nextM = m === 12 ? 1 : m + 1;
      return iso(nextY, nextM, lastDayOfMonth(nextY, nextM));
    }
    case 'day_of_month': {
      const nextY = m === 12 ? y + 1 : y;
      const nextM = m === 12 ? 1 : m + 1;
      const day = Math.min(term.dayOfMonth ?? 1, lastDayOfMonth(nextY, nextM));
      return iso(nextY, nextM, day);
    }
  }
}

/**
 * The due date for a bill on `docDate` for a supplier carrying `term` (or
 * none), under the company's switches — exactly `partnerDueDate`'s order of
 * decisions: terms off → due on issue; no term → the fallback days; else
 * the term.
 */
export function previewDueDate(input: {
  docDate: string;
  term: TermShape | null;
  paymentTermsOn: boolean;
  fallbackDays: number | null | undefined;
}): string {
  if (!input.paymentTermsOn) return input.docDate;
  if (input.term === null) {
    const days = input.fallbackDays ?? null;
    if (days === null || !Number.isInteger(days) || days <= 0) return input.docDate;
    return dueDateFromTerm({ method: 'days_after', days, dayOfMonth: null }, input.docDate);
  }
  return dueDateFromTerm(input.term, input.docDate);
}
